const API_BASE = '/api/compliance/vendor-risk';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface Vendor {
  id: number;
  company_id: number;
  name: string;
  service_category: string;
  description?: string;
  website?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  data_access_level: 'none' | 'public' | 'internal' | 'confidential' | 'restricted';
  risk_score?: number;
  risk_level?: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'under_review' | 'terminated' | 'pending';
  contract_start_date?: string;
  contract_renewal_date?: string;
  last_assessed?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

export interface VendorAssessment {
  id: number;
  vendor_id: number;
  assessed_by?: number;
  assessor_name?: string;
  likelihood: number;
  impact: number;
  risk_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  findings?: string;
  recommendations?: string;
  next_assessment_date?: string;
  created_at: string;
}

export interface VendorStats {
  total: number;
  active: number;
  under_review: number;
  terminated: number;
  high_risk: number;
  critical_risk: number;
  due_for_review: number;
}

export const vendorRiskApi = {
  async getVendors(params?: { status?: string; risk_level?: string; search?: string; page?: number; limit?: number }): Promise<{ vendors: Vendor[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.risk_level) q.set('risk_level', params.risk_level);
    if (params?.search) q.set('search', params.search);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async getVendor(id: number): Promise<Vendor> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async createVendor(data: Partial<Vendor>): Promise<Vendor> {
    const res = await fetch(API_BASE, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateVendor(id: number, data: Partial<Vendor>): Promise<Vendor> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async deleteVendor(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async getVendorStats(): Promise<VendorStats> {
    const res = await fetch(`${API_BASE}/stats`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getHighRiskVendors(): Promise<Vendor[]> {
    const res = await fetch(`${API_BASE}/high-risk`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getAssessments(vendorId: number): Promise<VendorAssessment[]> {
    const res = await fetch(`${API_BASE}/${vendorId}/assessments`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async createAssessment(vendorId: number, data: { likelihood: number; impact: number; findings?: string; recommendations?: string; next_assessment_date?: string }): Promise<VendorAssessment> {
    const res = await fetch(`${API_BASE}/${vendorId}/assessments`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
